
export const COLORS = [
  "primary",
  "secondary",
  "secondary-inverted",
  "black",
  "link",
  "white",
  "brown",
];

export const SIZES = ["xs", "sm", "md", "lg", "xl", "2xl", "3xl", "link"];

export const FONT_WEIGHTS = ["normal", "bold"];

export default {
  props: {
    to: { type: String, default: "" },
    normalCase: { type: Boolean, default: false },
    color: {
      type: String,
      default: "primary",
      validator: color => COLORS.includes(color),
    },
    colorHex: {
      type: String,
      default: null,
      validator: color =>
        color == null || /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color),
    },
    labelColorHex: {
      type: String,
      default: null,
      validator: color =>
        color == null || /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color),
    },
    hoverColorHex: {
      type: String,
      default: null,
      validator: color =>
        color == null || /^#(?:[A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color),
    },
    hoverWithFilterColor: { type: Boolean, default: false },
    size: {
      type: String,
      default: "md",
      validator: size => SIZES.includes(size),
    },
    responsive: { type: Boolean, default: false },
    block: { type: Boolean, default: false },
    icon: { type: Boolean, default: false },
    rounded: { type: Boolean, default: false },
    roundedCorner: { type: Boolean, default: true },
    fontWeight: {
      type: String,
      default: "bold",
      validator: fw => FONT_WEIGHTS.includes(fw),
    },
    wFull: { type: Boolean, default: false },
    external: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
  },

  computed: {
    componentData() {
      if (this.to) return { Tag: "nuxt-link", to: this.to };
      if (this.external) return { Tag: "a" };
      return { Tag: "button" };
    },
    baseColorClasses() {
      switch (this.color) {
        case "secondary":
          return "border text-white bg-teal-800 hover:bg-gray-100 hover:text-teal-800 hover:border-teal-800";
        case "secondary-inverted":
          return "border text-teal-800 bg-white hover:bg-teal-800 hover:text-white hover:border hover:border-teal-800";
        case "black":
          return "text-white bg-black hover:bg-gray-700";
        case "link":
          return "text-black text-sm font-medium border-b-2 border-gray-500 hover:border-gray-900 pb-1 px-0 mr-8";
        case "white":
          return "text-black bg-white hover:bg-gray-100 hover:border-gray-700";
        case "brown":
          return "text-white bg-brown-400 hover:bg-gray-700";
        default:
          return "text-white bg-gray-500 hover:bg-gray-700";
      }
    },
    getPaddingClass() {
      switch (this.size) {
        case "lg":
          return "px-5";
        case "xl":
          return "px-10";
        case "2xl":
          return "px-12";
        case "3xl":
          return "px-3 lg:px-18";
        case "link":
          return "px-0";
        default:
          return "px-3";
      }
    },
    buttonClasses() {
      const isLarge = this.size === "3xl";

      return [
        "transition-colors duration-500 ease-in-out text-xs text-center",
        this.compact
          ? "leading-none py-1 inline-flex items-center"
          : "leading-5 py-3",
        this.baseColorClasses,
        this.getPaddingClass,
        !isLarge && this.responsive ? "lg:px-20" : "",
        this.fontWeight === "normal" ? "font-normal" : "font-bold",
        this.block ? "w-full md:w-auto" : "",
        this.wFull ? "block" : "inline-block",
        this.normalCase ? "normal-case" : "uppercase",
        this.colorHex && "color-hex-button",
        this.hoverColorHex && "hover-color-hex-button",
        this.hoverWithFilterColor && "hover-color-filter",
        this.roundedCorner && "rounded-3xl",
      ]
        .filter(Boolean)
        .join(" ");
    },
    buttonStyles() {
      const styles = [];
      if (this.colorHex) {
        styles.push(`background-color: ${this.colorHex};`);
      }
      if (this.labelColorHex) {
        styles.push(`color: ${this.labelColorHex};`);
      }
      if (this.hoverColorHex) {
        styles.push(`--hover-bg-color: ${this.hoverColorHex};`);
      }
      return styles.join(" ");
    },
  },

  render(h) {
    const { Tag, ...propsData } = this.componentData;

    if (this.icon) {
      return <Tag>{this.$slots.default}</Tag>;
    }

    if (this.rounded) {
      return (
        <button
          class={[
            "rounded-full mr-2 mb-4 cursor-pointer focus:outline-none shadow",
            this.size === "md" ? "w-8 h-8" : "w-6 h-6",
          ]}
        >
          {this.$slots.default}
        </button>
      );
    }

    return (
      <Tag
        props={propsData}
        class={this.buttonClasses}
        style={this.buttonStyles}
      >
        {this.$slots.default}
      </Tag>
    );
  },
};
